import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  AddPromoCodesRequest,
  AddPromoCodesResponseData,
  AffiliatePromoCodesQuery,
  AffiliatePromoCodesResponseData,
  ApiSuccessEnvelope,
} from "../../types/affiliates";
import { getPrivateInstance } from "../_privateInstance";

const DEFAULT_LIMIT = 100;

const promoCodesPath = (affiliateId: string) =>
  API_ROUTES.GET_AFFILIATE_PROMO_CODES.replace(
    "{affiliate_id}",
    encodeURIComponent(affiliateId),
  );

export const fetchAffiliatePromoCodes = async (
  affiliateId: string,
  query: AffiliatePromoCodesQuery = {},
): Promise<AffiliatePromoCodesResponseData> => {
  const response = await getPrivateInstance().get<
    ApiSuccessEnvelope<AffiliatePromoCodesResponseData>
  >(promoCodesPath(affiliateId), {
    params: {
      limit: query.limit ?? DEFAULT_LIMIT,
      // First page is signalled by an absent cursor.
      ...(query.last_evaluated_key
        ? { last_evaluated_key: query.last_evaluated_key }
        : {}),
      // `q` / `status` are filtered server-side once BE extends
      // `AffiliatePromoCodeListQueryParams`. Until then BE silently
      // drops these (Pydantic extra="ignore") and the consumer falls
      // back to an in-memory filter. Drop nothing here when BE ships
      // — these lines already wire the params end-to-end.
      ...(query.status ? { status: query.status } : {}),
      ...(query.search?.trim() ? { q: query.search.trim() } : {}),
      // Date range scopes the per-code `metrics` block. BE enforces
      // both-or-neither, so only send the pair when complete.
      ...(query.start_date && query.end_date
        ? { start_date: query.start_date, end_date: query.end_date }
        : {}),
    },
  });
  return response.data.data;
};

export const useAffiliatePromoCodes = (
  affiliateId: string | undefined,
  query: AffiliatePromoCodesQuery = {},
): UseQueryResult<AffiliatePromoCodesResponseData, Error> =>
  useQuery<AffiliatePromoCodesResponseData, Error>({
    queryKey: [
      "affiliates",
      "promo-codes",
      affiliateId ?? "",
      query.limit ?? DEFAULT_LIMIT,
      query.last_evaluated_key ?? "",
      query.status ?? "",
      query.search?.trim() ?? "",
      query.start_date ?? "",
      query.end_date ?? "",
    ],
    queryFn: () => fetchAffiliatePromoCodes(affiliateId as string, query),
    enabled: !!affiliateId,
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });

export type AddPromoCodeInput = {
  affiliateId: string;
  payload: AddPromoCodesRequest;
};

const addPromoCodes = async ({
  affiliateId,
  payload,
}: AddPromoCodeInput): Promise<AddPromoCodesResponseData> => {
  const response = await getPrivateInstance().post<
    ApiSuccessEnvelope<AddPromoCodesResponseData>
  >(promoCodesPath(affiliateId), payload);
  return response.data.data;
};

export const useAddPromoCodes = (): UseMutationResult<
  AddPromoCodesResponseData,
  Error,
  AddPromoCodeInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["affiliates", "add-promo-codes"],
    mutationFn: addPromoCodes,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["affiliates", "promo-codes", variables.affiliateId],
      });
    },
  });
};

export { addPromoCodes };
